// @flow
import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import {
  compose,
  withHandlers,
  lifecycle,
  setPropTypes,
  withProps,
  branch,
  renderComponent
} from 'recompose'
import { throttle } from 'lodash'
import {
  setActiveEntity,
  checkIfMoreEntitiesNeeded,
  nextPage,
  searchCharities,
  searchAuctions,
  searchItems,
  clearActiveEntities,
  getActiveEntities,
  getActiveEntity,
  domains,
  getSearchItemsResource,
  hasMoreEntitiesToFetch,
  scrollToTop,
  getSearchAuctionsResource
} from '../index'
import {
  isCommInProgress,
  getCommunicationState,
  getLastRequestTime,
  COMM_STATES
} from '../../modules/communication'
import { t, convertDomainToKey } from '../../core/i18n'
import SearchResultsCharities from './SearchResultsCharities'
import SearchResultsAuctions from './SearchResultsAuctions'
import SearchResultsItems from './SearchResultsItems'
import SearchNoResults from '../../ui/views/search-no-results'
import Loading from '../../ui/views/loading'
import { currentFilter, isFiltersOpen } from '../layout/selectors'
import { toggleFilters, setFilter } from '../layout/actions'

const debouncedNextPage = throttle(nextPage, 100)
const MIN_REQUEST_DELAY = 1000

const withPropTypes = setPropTypes({
  domain: PropTypes.string
})

const getResource = (domain, auctionId, charityId) => {
  switch (domain) {
    case domains.AUCTIONS:
      return getSearchAuctionsResource(charityId)
    case domains.ITEMS:
      return getSearchItemsResource(auctionId, charityId)
    default:
      return domain
  }
}

const makeMapStateToProps = (state, props) => {
  const auctionId = props.match.params.auctionId
  const charityId = props.match.params.charityId
  const resource = getResource(props.domain, auctionId, charityId)
  const activeEntitySelector = getActiveEntity(resource)

  return {
    isLoading: isCommInProgress(resource, state),
    commState: getCommunicationState(resource, state),
    lastRequestedOn: getLastRequestTime(resource, state),
    entities: getActiveEntities(resource, state),
    hasMore: hasMoreEntitiesToFetch(resource, state),
    id: activeEntitySelector(state),
    auctionId,
    charityId,
    resource
  }
}
const withSearchData = connect(makeMapStateToProps, {
  searchCharities,
  searchItems,
  searchAuctions,
  setActiveEntity,
  checkIfMoreEntitiesNeeded,
  nextPage: debouncedNextPage
})

const withScrollAndSlider = withHandlers({
  handleScroll: props => e => {
    const { domain, auctionId, charityId, nextPage, isLoading, hasMore } = props
    if (!isLoading && hasMore) {
      console.log('calling nextPage', domain, auctionId, charityId)
      nextPage(domain, auctionId, charityId)
    }
    /* const currentScroll = e.target.scrollTop
    const containerHeight = e.target.children[0].clientHeight */

    /* if (currentScroll > containerHeight - MIN_SCROLL) {
      console.log('Scroll?')
      props.pageUp(domain)
    } */
  },
  handleSliderChange: props => newId => {
    const {
      domain,
      auctionId,
      charityId,
      setActiveEntity,
      checkIfMoreEntitiesNeeded,
      id
    } = props
    if (newId !== id) setActiveEntity(props.resource, newId)
    checkIfMoreEntitiesNeeded(domain, newId, auctionId, charityId)
  }
})

const fetchOnMount = lifecycle({
  fetchData() {
    const {
      domain,
      auctionId,
      charityId,
      searchParams,
      searchItems,
      searchAuctions,
      searchCharities
    } = this.props

    if (domain === domains.ITEMS) {
      searchItems(auctionId, charityId, searchParams)
    } else if (domain === domains.AUCTIONS) {
      searchAuctions(charityId, searchParams)
    } else {
      searchCharities(searchParams)
    }
  },
  componentDidMount() {
    const { commState, isLoading, lastRequestedOn } = this.props
    if (!isLoading && commState === COMM_STATES.NotAsked) {
      this.fetchData()
      return
    }
    if (!isLoading && Date.now() - lastRequestedOn > MIN_REQUEST_DELAY) {
      this.fetchData()
    }
    scrollToTop()
  }
})

const withTranslations = withProps(
  (props: { domain: string, entities: Object }) => ({
    translations: {
      results: t(`common:${convertDomainToKey(props.domain)}`, {
        count: props.entities ? props.entities.length : 0
      })
    }
  })
)

const withLoading = branch(props => props.isLoading, renderComponent(Loading))

const withFilter = connect(
  state => ({
    isFiltersVisible: Boolean(isFiltersOpen(state)),
    currentFilter: currentFilter(state)
  }),
  {
    toggleFilters,
    setFilter,
    searchItems,
    clearActiveEntities
  }
)

const withNoResults = branch(
  props =>
    /* props.commState === COMM_STATES.Success &&  */ !props.entities.length,
  renderComponent(SearchNoResults)
)

const decorate = compose(
  withPropTypes,
  withSearchData,
  fetchOnMount,
  withLoading,
  withFilter,
  withNoResults,
  withScrollAndSlider,
  withTranslations
)

const SearchResults = props => {
  const { domain } = props
  const { CHARITIES, AUCTIONS, ITEMS } = domains
  return (
    (domain === CHARITIES && <SearchResultsCharities {...props} />) ||
    (domain === AUCTIONS && <SearchResultsAuctions {...props} />) ||
    (domain === ITEMS && <SearchResultsItems {...props} />)
  )
}

export default decorate(SearchResults)



// WEBPACK FOOTER //
// ./src/services/search-results/SearchResults.js